import { Badge } from '@/components/ui/badge';
import { getShiftColour, SHIFT_PILL_CLASSES } from '@/domain/templates/shift-colours';
import { cn } from '@/lib/utils';

interface ShiftPillProps {
  /** Shift type name, used for the label and the colour. */
  type: string;
  /** Formatted time range, e.g. `14:00–22:00`. */
  time: string;
  /** Ghost styling for the pattern currently being edited. */
  draft?: boolean;
  /**
   * Nth occurrence of this shift type in the preview (0-based). Repeats get a
   * progressively stronger overlay so two patterns of the same type don't read
   * as identical. Ignored for draft pills.
   */
  variant?: number;
  /** Unfilled ("short") treatment — a dashed destructive pill. */
  short?: boolean;
  /** Optional third line, e.g. the assigned worker's name or a "Short" label. */
  subtitle?: string;
}

/**
 * A single per-person shift in the Build workspace live preview — one pill per
 * required head (matching one `ProposedShift` per head). Built on the EDS
 * `Badge`; colours come from the `--shift-*` theme tokens via
 * {@link SHIFT_PILL_CLASSES} so the pill follows light/dark mode and host
 * theming. Draft pills use the subtle/dashed treatment.
 */
export function ShiftPill({
  type,
  time,
  draft = false,
  variant = 0,
  short = false,
  subtitle,
}: ShiftPillProps) {
  const classes = SHIFT_PILL_CLASSES[getShiftColour(type)];
  const overlay = draft || short ? undefined : variantOverlay(variant);

  return (
    <Badge
      variant="outline"
      data-testid="shift-pill"
      data-draft={draft ? 'true' : 'false'}
      data-short={short ? 'true' : 'false'}
      data-shift-variant={variant}
      // A decorative translucent overlay differentiates repeats; it sits over
      // the token-driven background colour (not a replacement for it).
      style={overlay ? { backgroundImage: overlay } : undefined}
      className={cn(
        'min-h-[48px] w-full flex-col items-stretch justify-start gap-0 rounded-[3px] px-2 pt-0.5 pb-0 text-left leading-tight',
        short
          ? 'border-dashed border-destructive/60 bg-destructive/5 text-destructive'
          : draft
            ? cn(classes.draft, 'border-dashed')
            : classes.solid,
      )}
    >
      <span className="block text-sm font-semibold break-words">{type}</span>
      <span className="block text-sm font-semibold opacity-90">{time}</span>
      {subtitle !== undefined && (
        <span className="block truncate text-sm font-medium opacity-90">{subtitle}</span>
      )}
    </Badge>
  );
}

/**
 * White-wash overlay for the Nth occurrence of a shift type: none for the
 * first, a light then stronger wash for the next two, a diagonal stripe beyond.
 */
function variantOverlay(variant: number): string | undefined {
  if (variant === 1) return 'linear-gradient(rgba(255,255,255,0.16), rgba(255,255,255,0.16))';
  if (variant === 2) return 'linear-gradient(rgba(255,255,255,0.30), rgba(255,255,255,0.30))';
  if (variant >= 3) {
    return 'repeating-linear-gradient(45deg, rgba(255,255,255,0.10) 0 5px, rgba(255,255,255,0.28) 5px 10px)';
  }
  return undefined;
}
